import clsx from 'clsx';
import { SkipToContent } from './components/skip-to-content';
import { useAuthQuery } from '@/services/auth';
import { AppErrorFallback } from '@/components/app-error-boundary/app-error-fallback';
import { useListWorkspaces, useSampleWorkspace } from '@/features/workspaces';
import {
  announceCallbackComplete,
  clearAttemptedTransitionUrl,
  getAttemptedTransitionUrl,
  setAttemptedTransitionUrl,
  subscribeToRefreshWorkspaces,
} from '@customerio/hydra-nav-state';
import { useRefreshAuthSubscription } from './use-refresh-auth';
import { useRoleDefinitions } from '@/features/roles';
import { Suspense, useEffect, useCallback, useMemo, useRef } from 'react';
import { Outlet, useLocation } from '@/services/router/compat';
import { useRecordLastVisitedRoute } from '@/services/session/last-visited-route';
import { appendMasqParam } from '@/utils/masq';
import { MainNavigation } from './components/main-navigation';
import { resolveNavCollapseTrigger } from './components/main-navigation/nav-collapse-interaction';
import { MainNavigationWrapper, NAV_COLLAPSED_LOCAL_STORAGE_KEY } from './components/main-navigation/wrapper';
import { isEmptyString } from '@/utils/string';
import { RecordPerformanceMetric } from './components/record-performance-metric';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { PerformanceMetric, PerformanceProvider } from '@/context/performance';
import { getIsAgentHandoffLoaderShowing } from '@/features/onboarding';
import { SidebarRoot } from '@/features/sidebar';
import { HelpSidebar } from '@/features/help-sidebar';
import { GlobalCSSVariable, useThemeContext } from '@/context/theme';
import { appContainerFadedOutStore, appContainerOutletRenderedStore } from '@/stores/root';
import { analytics } from '@/services/analytics/client';
import { useAnalytics } from '@/services/analytics';
import { billing } from '@/services/billing';
import { useIsSettings } from '@/services/router/use-is-settings';
import { VideoLibraryProvider } from '@/features/video-library';
import tracking from '@/utils/tracking-scripts';
import { InlineStack, Stack, PageLoader, SideNavProvider } from '@customerio/pluma-components/react';
import { useStore } from '@nanostores/react';
import { session } from '@/services/session';
import { AppErrorBoundary } from '@/components/app-error-boundary';
import {
  handleExpiredSessionFallback,
  handleWorkspaceSessionMismatch,
  useExpiredSessionSnackbar,
  useWorkspaceMismatchSnackbar,
  PENDING_SESSION_SWITCH_KEY,
} from '@/features/account-switcher';
import { SettingsNavigation } from './components/settings-navigation';
import { TopNavigation } from './components/top-navigation';
import { SampleWorkspaceBorder } from './components/top-navigation/components/sample-workspace';
import { WorkspaceStripe } from './components/workspace-stripe';
import {
  sentrySetDataCenter,
  sentrySetUser,
  setSentryFullStorySessionUrl,
  setSentryMasqueradingContext,
} from '@/utils/sentry';
import { Navigate } from '@/utils/navigate';
import { Region } from '@/services/auth/types';
import { AgentLauncher, CopilotSessionProvider, CopilotSidebarProvider } from '@/features/copilot';
import { getStorageValue } from '@/hooks/browser/use-local-storage';
import { AiEnabledGuard } from '@/components/guards';
import { MasqueradeBanner } from '@/features/masquerade-banner';
import * as styles from './index.css';
import { useCacheInvalidation } from '@/hooks/use-cache-invalidation';
import { useMountEffect } from '@/hooks/state/use-mount-effect';
import { usePageViewTracking } from '@/hooks/use-page-view-tracking';
import { useReleaseNotification } from './use-release-notification';
import { useReauthPrompt } from './use-reauth-prompt';
import { useReleaseNotificationModal } from './components/release-notification-modal';
import { WorkspaceModeBanners } from './components/workspace-mode-banners';
import { ViewerBanner } from './components/viewer-banner';

let didIdentify = false;

function AuthenticatedContent() {
  useCacheInvalidation();
  useExpiredSessionSnackbar();
  useWorkspaceMismatchSnackbar();
  const { showModal: showReleaseModal, reload: reloadForRelease } = useReleaseNotification();
  useReleaseNotificationModal({ isReleaseReady: showReleaseModal, onReload: reloadForRelease });

  const auth = useAuthQuery();
  const workspaces = useListWorkspaces();
  const currentWorkspace = useCurrentWorkspace();
  const appContainerFadedOut = useStore(appContainerFadedOutStore);
  const isSessionAuthenticated = useStore(session.isAuthenticated);
  const appContainerOutletRendered = useStore(appContainerOutletRenderedStore);
  const themeContext = useThemeContext();
  const location = useLocation();
  // Record the current authenticated route so an explicit logout can return the
  // user here after re-login (Ember's `lastVisitedRoute` → `/login?origin=…`).
  useRecordLastVisitedRoute(location.pathname);
  const { isSettingsPage, isAnySettingsPage } = useIsSettings();
  const { isSampleWorkspace } = useSampleWorkspace();
  const roleDefinitions = useRoleDefinitions();

  const isHandoffCollapse = getIsAgentHandoffLoaderShowing();
  const isSideNavDefaultCollapsed =
    isHandoffCollapse || getStorageValue<boolean>(NAV_COLLAPSED_LOCAL_STORAGE_KEY, false);

  const { track } = useAnalytics();
  const collapsedAtRef = useRef<number | null>(isSideNavDefaultCollapsed === true ? Date.now() : null);
  const sessionExpandsAfterCollapseRef = useRef(0);

  const onSideNavCollapseChange = useCallback(
    (isCollapsed: boolean) => {
      // Drag previews flip this repeatedly and the release can re-commit an
      // unchanged value; only committed decisions may reach the tracker.
      const trigger = resolveNavCollapseTrigger();
      if (trigger == null) {
        return;
      }
      if (isCollapsed) {
        collapsedAtRef.current = Date.now();
        void track('leftNav', 'collapsed', {
          Trigger: trigger,
          'Session Nav Opens After Collapse': sessionExpandsAfterCollapseRef.current,
        });
      } else {
        sessionExpandsAfterCollapseRef.current += 1;
        const timeCollapsedMs = collapsedAtRef.current != null ? Date.now() - collapsedAtRef.current : 0;
        collapsedAtRef.current = null;
        void track('leftNav', 'expanded', {
          Trigger: trigger,
          'Time Collapsed Ms': timeCollapsedMs,
        });
      }
    },
    [track],
  );

  useEffect(() => {
    // Set the top nav height to 44px to make sure child apps
    // have the correct CSS variable values
    themeContext.setVariable(GlobalCSSVariable.TopNavHeight, '44px');
  }, [themeContext]);

  useRefreshAuthSubscription({
    refetch: auth.refetch,
    currentWorkspaceId: currentWorkspace?.id != null ? `${currentWorkspace.id}` : null,
    locationPathname: location.pathname,
  });

  const isRefreshingAuthSampleWorkspace = useRef(false);
  useEffect(() => {
    // Switching to the sample workspace doesn't trigger
    // `subscribeToRefreshAuth`. If the current user doesn't have a role
    // for the sample workspace, refresh the auth data.
    if (currentWorkspace?.id != null && isSampleWorkspace(currentWorkspace.id)) {
      const workspaceId = currentWorkspace.id;
      const hasRoleForSampleWorkspace = auth.data?.user?.roles?.some((r) => r.workspace_id === workspaceId) ?? false;

      if (!hasRoleForSampleWorkspace && !auth.isLoading && !isRefreshingAuthSampleWorkspace.current) {
        const callback = async () => {
          isRefreshingAuthSampleWorkspace.current = true;
          try {
            await auth.refetch();
          } finally {
            isRefreshingAuthSampleWorkspace.current = false;
          }
        };
        callback();
      }
    }
  }, [isSampleWorkspace, currentWorkspace?.id, auth.data?.user?.roles, auth.isLoading]);

  useMountEffect(() => {
    return subscribeToRefreshWorkspaces(async (event) => {
      try {
        await workspaces.refetch();
      } finally {
        announceCallbackComplete(event.detail.id);
      }
    });
  });

  useEffect(() => {
    const showTopColor = !isSettingsPage && currentWorkspace != null && !isEmptyString(currentWorkspace.color);

    if (showTopColor && currentWorkspace != null) {
      themeContext.setVariable(GlobalCSSVariable.WorkspaceBorderColor, currentWorkspace.color);
      themeContext.setVariable(GlobalCSSVariable.WorkspaceBorderHeight, '6px');
    } else {
      themeContext.setVariable(GlobalCSSVariable.WorkspaceBorderHeight, '0px');
    }
  }, [currentWorkspace, isSettingsPage]);

  // Additional Sentry setup once the user is authenticated
  useEffect(() => {
    if (auth.isSuccess === false || auth.data == null) {
      return;
    }

    const { account, user } = auth.data;

    if (account == null || user == null) {
      return;
    }

    sentrySetUser(account, user);
    sentrySetDataCenter(account.region === Region.EU ? 'eu' : 'us');
    setSentryFullStorySessionUrl();
    setSentryMasqueradingContext(account.is_masq);
  }, [auth.data, auth.isSuccess]);

  // Bootstrap billing/trial data independently of the embedded Ember app. Nav
  // chrome that reads it (e.g. the trial upgrade CTA) is always mounted, including
  // on routes Ember never mounts on, so Hydra can no longer rely on Ember's own
  // `billing.initialize.perform(...)` call to have populated this data first.
  //
  // `billing.initialize` latches `initialized = true` after its first success and
  // no-ops on later calls unless `refresh` is passed, so switching to a different
  // account (e.g. masquerade) must explicitly request a refresh — otherwise nav
  // chrome would keep showing the previous account's billing/trial state.
  const previousAccountIdRef = useRef<number | null>(null);
  useEffect(() => {
    const accountId = auth.data?.account?.id;
    if (accountId == null) {
      return;
    }

    const isAccountChange = previousAccountIdRef.current != null && previousAccountIdRef.current !== accountId;
    previousAccountIdRef.current = accountId;

    // On an in-app account switch (e.g. masquerade) there's no page reload, so the billing service's
    // module-level atoms survive and can leak the previous account's data. Reset them to a clean slate
    // before re-initializing; first-mount (not an account change) keeps its existing behavior.
    if (isAccountChange) {
      billing.handleAccountSwitch();
    }

    void billing.initialize.perform({ ignoreErrors: true, refresh: isAccountChange });
  }, [auth.data?.account?.id]);

  // inject tracking scripts once the user is authenticated
  useEffect(() => {
    if (auth.isSuccess === true && auth.data != null && !didIdentify) {
      didIdentify = true;
      const { user } = auth.data;

      // identify
      void analytics?.identify(`${user?.id}`, {
        id: `${user?.id}`,
        email: user?.email,
      });
    }
  }, [auth.data, auth.isSuccess]);

  // In-place re-auth on a session rejection: keep the app mounted and
  // re-authenticate through the managed modal instead of redirecting to /login
  // and losing work. The hook owns the modal open/close side effect and returns
  // the two derivations the render path below branches on.
  const { hasLiveSessionRejection, rejectedUser } = useReauthPrompt({
    auth,
    isSessionAuthenticated,
    currentWorkspaceId: currentWorkspace?.id,
  });

  // Fire one CDP page view per navigation. `usePageViewTracking` keys on the
  // `pathname + searchStr` string and dedupes via a ref; keying an effect on the
  // raw `location` object instead re-fired on every boot re-render, flooding CDP
  // and starving boot fetches on connection-limited browsers (iOS Safari).
  usePageViewTracking(() => void analytics?.page());

  // clean tracking scripts + analytics once the component is unmounted
  useMountEffect(() => {
    return () => {
      tracking.cleanup();
      void analytics?.reset();
    };
  });

  // Using useMemo to make sure the attempted url setting runs before rendering,
  // i.e. before the Navigate component is rendered. A rejection can retain
  // last-good `data` (the queryFn throws instead of overwriting the cache), so
  // the redirect-bound states are "no data" OR "rejected".
  useMemo(() => {
    if (auth.isLoading === true || auth.isFetching === true) {
      return;
    }

    if (auth.data == null || hasLiveSessionRejection) {
      const attemptedUrl = window.location.href.replace(/\/$/, '');
      if (getAttemptedTransitionUrl() == null && attemptedUrl !== window.location.origin) {
        setAttemptedTransitionUrl(attemptedUrl);
      }
    } else {
      clearAttemptedTransitionUrl();
    }
  }, [auth.data, hasLiveSessionRejection, auth.isLoading, auth.isFetching]);

  if (auth.isLoading === true || (auth.data == null && auth.isFetching === true)) {
    return <PageLoader />;
  }

  // The server refused credentials (session dead, workspace off-limits, or a
  // bad masq target): run the recovery fallbacks first. When none applies and
  // a user was previously loaded (`rejectedUser`, above), keep the app mounted
  // from the retained last-good data and re-authenticate in place (the managed
  // modal opened by the effect above) — matching the embedded Ember app's
  // behavior. Otherwise send to login: masq rejections bounce home from there
  // (the latch stays `true`, the intended recovery), and tabs without a user
  // context have nothing to render behind a re-auth prompt (Ember's
  // `auth.user == null -> logout` branch).
  if (hasLiveSessionRejection) {
    if (handleExpiredSessionFallback()) {
      return <PageLoader />;
    }
    if (handleWorkspaceSessionMismatch()) {
      return <PageLoader />;
    }
    if (rejectedUser == null) {
      return <Navigate to="/login" saveCurrentUrl={true} />;
    }
  }

  // Transient failure (5xx/network) with nothing to render from — a first
  // load during a backend blip. Show a retry state instead of bouncing to
  // /login: the session may be perfectly valid, and the login bounce is what
  // used to ping-pong (the 5xx flavor of FPS-2996). With retained data the
  // app keeps rendering below and the next refetch trigger recovers.
  if (auth.isError && auth.data == null) {
    return (
      <AppErrorFallback
        error={auth.error}
        resetErrorBoundary={() => {
          void auth.refetch();
        }}
      />
    );
  }

  if (auth.data == null || auth.data?.account == null || auth.data?.user == null) {
    if (handleExpiredSessionFallback()) {
      return <PageLoader />;
    }
    if (handleWorkspaceSessionMismatch()) {
      return <PageLoader />;
    }
    return <Navigate to="/login" saveCurrentUrl={true} />;
  } else {
    // Make sure we clear the pending switch marker
    sessionStorage.removeItem(PENDING_SESSION_SWITCH_KEY);
  }

  if (workspaces.isLoading) {
    return <PageLoader />;
  }

  // Hold the tree while roles load: `checkPermission` denies every
  // workspace-role permission until they land, which renders a deny-then-allow
  // flicker.
  if (roleDefinitions.isPending) {
    return <PageLoader />;
  }

  // Without roles the app can only deny, so it would render as a workspace the
  // user has no rights in. Show the same retry state the auth failure above
  // uses instead of a silently crippled app.
  if (roleDefinitions.isError) {
    return (
      <AppErrorFallback
        error={roleDefinitions.error}
        resetErrorBoundary={() => {
          roleDefinitions.refetch();
        }}
      />
    );
  }

  const { account, user } = auth.data;

  // Permissions are provided above Pluma by DerivedPermissionsProvider (see layouts/root),
  // so authenticated pages and Pluma managed modals share the same permissions context.

  // If we're masqing but the current user doesn't have masq permissions set, or
  // the permissions are "none", redirect to settings page (which is accessible
  // to all users who can masq)
  if (account.is_masq === true && (user.noMasqAccess === true || user.masqLevelNotSet === true) && !isAnySettingsPage) {
    return <Navigate to={appendMasqParam('/settings')} />;
  }

  return (
    <CopilotSidebarProvider>
      <CopilotSessionProvider>
        <VideoLibraryProvider>
          <SideNavProvider defaultIsCollapsed={isSideNavDefaultCollapsed} onCollapseChange={onSideNavCollapseChange}>
            <RecordPerformanceMetric performanceMetric={PerformanceMetric.Authenticated} />
            <WorkspaceStripe />
            <SkipToContent />
            <MasqueradeBanner />
            <ViewerBanner />
            <>
              <WorkspaceModeBanners />
              <Stack flexGrow="1" minH="0">
                <TopNavigation isCollapsible={!isAnySettingsPage} />
                <InlineStack alignBlock="stretch" flexGrow="1" minH="0" w="full" h="full">
                  <MainNavigationWrapper isCollapsible={!isAnySettingsPage} shouldPersistCollapsed={!isHandoffCollapse}>
                    {!isAnySettingsPage ? <MainNavigation /> : null}
                    {isAnySettingsPage ? <SettingsNavigation /> : null}
                  </MainNavigationWrapper>
                  <div style={{ display: 'flex', minHeight: 0, minWidth: 0, flex: 1 }}>
                    <div className={clsx(styles.layoutContent, styles.layoutContentLayoutRefresh)}>
                      <div
                        id="embedded-app-container"
                        className={clsx(styles.layoutContentInner, styles.layoutContentInnerLayoutRefresh)}
                        style={{
                          opacity: appContainerFadedOut ? 0 : 1,
                        }}
                      >
                        {appContainerOutletRendered ? (
                          <AppErrorBoundary resetKeys={[location.pathname]}>
                            <Suspense fallback={<PageLoader />}>
                              <Outlet />
                            </Suspense>
                          </AppErrorBoundary>
                        ) : null}
                      </div>
                      <AiEnabledGuard>
                        <AgentLauncher />
                      </AiEnabledGuard>
                    </div>
                    <SidebarRoot />
                    <HelpSidebar />
                  </div>
                </InlineStack>
              </Stack>
            </>
            <SampleWorkspaceBorder />
          </SideNavProvider>
        </VideoLibraryProvider>
      </CopilotSessionProvider>
    </CopilotSidebarProvider>
  );
}

export default function Authenticated() {
  // `PerformanceProvider` stays above `AuthenticatedContent` so it keeps its
  // metrics across the early returns below. Inside the content it would remount
  // on every flip back to a loader and lose the boot timings it collects.
  return (
    <PerformanceProvider>
      <AuthenticatedContent />
    </PerformanceProvider>
  );
}
